(ns ui.view.transaction-process.cards.verifications
  (:require
   [common.localization :refer [tr]]
   [re-frame.core :as rf]
   [ui.components.button.view :as button]
   [ui.dom-globals :as dg]
   [ui.util :as uu]
   [ui.view.transaction-process.cards.card-header :as card-header]
   [ui.view.transaction-process.event :as event]
   [ui.view.transaction-process.modals.attestation.event :as attestation-event]
   [ui.view.transaction-process.sub :as sub]
   [ui.view.transaction-process.util :as tp-util]))

(defn- outcome-badge
  [entry attestation]
  (let [outcome (:attestation/outcome attestation)]
    (cond
      (nil? outcome)
      [tp-util/status-badge {:label (tr :not-started) :variant :neutral}]

      (tp-util/attestation-display-label entry attestation)
      [tp-util/status-badge {:label   (tp-util/attestation-display-label entry attestation)
                             :variant (tp-util/attestation-outcome-variant entry outcome)}]

      :else
      [tp-util/render-error {:context "Unmapped :attestation/outcome keyword"
                             :value outcome}])))

(defn- verification-row
  [{:keys [entry attestation can-write? selected? on-click on-review]}]
  (let [outcome (:attestation/outcome attestation)
        started? (some? attestation)
        has-conditions? (tp-util/attestation-requires-conditions? entry outcome)]
    [:div {:class (uu/cm "border rounded-lg p-4 flex items-center justify-between"
                         (when started? "cursor-pointer hover:bg-gray-50")
                         (when selected? "ring-2 ring-primary-regular ring-offset-1")
                         (case (tp-util/attestation-outcome-variant entry outcome)
                           :success "border-primary-regular/30 bg-primary-regular/5"
                           :warning "border-yellow-300 bg-yellow-50/50"
                           :danger  "border-red-300 bg-red-50/50"
                           "border-gray-200"))
           :on-click (when started? on-click)
           :aria-current (when selected? "true")}
     [:div
      [:span {:class "text-sm m-0 text-gray-900 block"}
       (:slot/label entry)]
      [:div {:class "mt-1 flex items-center gap-1.5"}
       [outcome-badge entry attestation]
       (when (:attestation/draft? attestation)
         [tp-util/status-badge {:label (tr :draft) :variant :warning}])]]
     [:div {:class "flex items-center gap-2"}
      (when (and (not started?) can-write?)
        [button/sm-secondary-hollow
         {:on-click on-review}
         (tr :review)])
      (when has-conditions?
        [button/sm-secondary-hollow
         {:on-click (fn [e]
                      (dg/stop-propagation e)
                      (on-click))}
         (tr :transaction-process/review-conditions)])
      (when started?
        [:svg {:class "w-4 h-4 text-gray-400"
               :xmlns "http://www.w3.org/2000/svg"
               :viewBox "0 0 24 24"
               :fill "none"
               :stroke "currentColor"
               :stroke-width "2"
               :stroke-linecap "round"
               :stroke-linejoin "round"}
         [:polyline {:points "9 6 15 12 9 18"}]])]]))

(defn condition-bearing-slot?
  "True when a resolved verification slot has an attestation whose configured
   outcome requires conditions."
  [{:keys [entry instance]}]
  (and (some? instance)
       (tp-util/attestation-requires-conditions?
        entry
        (:attestation/outcome instance))))

(defn- render-attestation-row
  "Render one attestation slot row. `slot` is the resolved
   {:slot-kw :entry :instance}. Normally hidden when the slot is not required;
   `include-existing?` keeps an existing optional attestation visible on summary
   cards. Visibility rules still hide empty slots but never hide existing data."
  [{:keys [slot can-write? selected-slot-kw ctx lookup include-existing?]}]
  (let [{:keys [slot-kw entry instance]} slot
        label     (:slot/label entry)
        required? (tp-util/resolve-required? {:entry entry :lookup lookup})
        visible?  (tp-util/slot-visible? lookup entry)]
    (when (and (or required? (and include-existing? (some? instance)))
               (or visible? (some? instance)))
      ^{:key slot-kw}
      [verification-row
       {:entry       entry
        :attestation instance
        :can-write?  can-write?
        :selected?   (= slot-kw selected-slot-kw)
        :on-click    (fn []
                       (rf/dispatch [::event/set-detail-panel ctx
                                     {:panel-type :verification
                                      :item       {:slot-kw            slot-kw
                                                   :entry              entry
                                                   :attestation-entity instance
                                                   :label              label}}]))
        :on-review   (fn []
                       (rf/dispatch [::attestation-event/open ctx
                                     {:slot-kw                 slot-kw
                                      :attestation-type-label  label
                                      :prefill                 (:slot/prefill entry)}]))}])))

(defn view
  [{:keys [card ctx]}]
  (let [matched @(rf/subscribe [::sub/matched-attestations ctx])
        process  @(rf/subscribe [::sub/process ctx])
        lookup process
        selected-slot-kw @(rf/subscribe [::sub/selected-verification-slot-kw ctx])
        can-write-attestations? @(rf/subscribe [::sub/can-write-field? ctx :attestation/outcome])
        conditions-only? (boolean (:card/conditions-only? card))
        ;; slot-kw -> resolved {:slot-kw :entry :instance}
        by-slot (->> matched
                     (map (fn [m] [(:slot-kw m) m]))
                     (into {}))
        slot-visible? (fn [slot]
                        (and (or (tp-util/slot-visible? lookup (:entry slot))
                                 (some? (:instance slot)))
                             (or (not conditions-only?)
                                 (condition-bearing-slot? slot))))
        ;; The layout drives presentation: review/approval cards carry
        ;; `:card/groups` (each {:group/id :group/label :group/slots}); the signing
        ;; card carries a flat `:card/slots`. Iterate the layout in order.
        groups (or (:card/groups card)
                   (when-let [slots (:card/slots card)]
                     [{:group/id nil :group/slots slots}]))
        rendered-groups
        (->> groups
             (keep (fn [{:keys [group/id group/label group/slots]}]
                     (let [rows (->> slots
                                     (keep by-slot)
                                     (filter slot-visible?)
                                     (keep (fn [slot]
                                             (render-attestation-row
                                              {:slot       slot
                                               :can-write? can-write-attestations?
                                               :selected-slot-kw selected-slot-kw
                                               :ctx        ctx
                                               :lookup     lookup
                                               :include-existing? conditions-only?})))
                                     doall)]
                       (when (seq rows)
                         {:group/id id :group/label label :rows rows})))))]
    ;; Hide the whole card when no slots render — e.g. the Signing stage's Lux-only
    ;; verifications card on a non-Lux fund.
    (when (seq rendered-groups)
      [:div {:class "bg-white rounded-lg border border-gray-200 p-6"}
       [card-header/view {:card card}]
       [:div {:class "flex flex-col gap-4"}
        (doall
         (for [{:keys [group/id group/label rows]} rendered-groups]
           ^{:key (or id :ungrouped)}
           [:div
            (when label
              [:h4 {:class "text-sm m-0 font-medium text-gray-700 mb-2"} label])
            (into [:div {:class "flex flex-col gap-2"}] rows)]))]])))
